// ─── routerAgent.js ───────────────────────────────────────────────────────────
// Rule-based query router — decides which stores are relevant for a query.
// Zero API cost — pure keyword matching.
//
// Usage in data.js:
//   import { routeQuery } from './agents/routerAgent';
//   const storeIds = routeQuery(query);          // e.g. ["amazon","walmart","target"]
//   // then only fire agents for those stores
// ─────────────────────────────────────────────────────────────────────────────

const ALL_STORES = ['amazon', 'walmart', 'homedepot', 'target', 'ulta', 'sephora', 'macys'];

// ── Category → store mapping ──────────────────────────────────────────────────
// Each category defines which stores carry those products.
// Stores NOT in the list are skipped — saving one API call each.
const CATEGORY_ROUTES = [

  // ── Beauty, personal care, health ─────────────────────────────────────────
  {
    name: 'beauty',
    stores: ['amazon', 'walmart', 'target'],
    keywords: [
      'cleanser', 'face wash', 'facial', 'moisturizer', 'serum', 'toner',
      'sunscreen', 'spf', 'makeup', 'foundation', 'mascara', 'lipstick',
      'eyeshadow', 'concealer', 'blush', 'primer', 'bronzer', 'highlighter',
      'shampoo', 'conditioner', 'hair mask', 'hair oil', 'dry shampoo',
      'body wash', 'lotion', 'body cream', 'deodorant', 'antiperspirant',
      'perfume', 'cologne', 'fragrance', 'nail polish', 'nail care',
      'clean & clear', 'neutrogena', 'cerave', 'olay', 'dove', 'nivea',
      'l\'oreal', 'maybelline', 'revlon', 'covergirl', 'nyx', 'elf cosmetics',
      'acne', 'skincare', 'skin care', 'anti-aging', 'retinol', 'hyaluronic',
    ],
  },

  // ── Health, medicine, vitamins ─────────────────────────────────────────────
  {
    name: 'health',
    stores: ['amazon', 'walmart', 'target'],
    keywords: [
      'vitamin', 'supplement', 'protein powder', 'probiotic', 'omega',
      'melatonin', 'collagen', 'zinc', 'magnesium', 'calcium', 'iron',
      'medicine', 'pain relief', 'ibuprofen', 'tylenol', 'advil', 'aspirin',
      'bandage', 'first aid', 'thermometer', 'blood pressure', 'glucose',
      'pharmacy', 'prescription', 'otc', 'cold medicine', 'allergy relief',
    ],
  },

  // ── Electronics, phones, computers ────────────────────────────────────────
  {
    name: 'electronics',
    stores: ['amazon', 'walmart', 'target'],
    keywords: [
      'iphone', 'samsung', 'android', 'smartphone', 'phone case',
      'laptop', 'macbook', 'chromebook', 'tablet', 'ipad',
      'headphone', 'earbuds', 'airpods', 'speaker', 'soundbar',
      'tv', 'television', 'monitor', 'smart tv', 'oled', 'qled',
      'camera', 'gopro', 'lens', 'tripod', 'drone',
      'keyboard', 'mouse', 'webcam', 'microphone', 'gaming',
      'playstation', 'xbox', 'nintendo', 'switch', 'controller',
      'charger', 'cable', 'usb', 'hdmi', 'adapter', 'power bank',
      'smartwatch', 'apple watch', 'fitbit', 'garmin',
      'router', 'wifi', 'modem', 'smart home', 'alexa', 'google home',
      'ring', 'nest', 'security camera', 'doorbell',
    ],
  },

  // ── Tools, hardware, building ──────────────────────────────────────────────
  {
    name: 'tools',
    stores: ['amazon', 'homedepot', 'walmart'],
    keywords: [
      'drill', 'screwdriver', 'wrench', 'hammer', 'saw', 'circular saw',
      'jigsaw', 'miter saw', 'table saw', 'sander', 'grinder', 'router',
      'nail gun', 'staple gun', 'level', 'tape measure', 'utility knife',
      'dewalt', 'milwaukee', 'makita', 'ryobi', 'ridgid', 'bosch', 'craftsman',
      'tool set', 'tool kit', 'toolbox', 'workbench', 'ladder', 'scaffold',
      'socket set', 'ratchet', 'torque', 'impact driver', 'impact wrench',
      'plumbing', 'pipe', 'faucet', 'toilet', 'sink', 'shower', 'drain',
      'electrical', 'wire', 'outlet', 'switch plate', 'breaker', 'conduit',
      'lumber', 'plywood', 'drywall', 'cement', 'concrete', 'mortar', 'grout',
      'insulation', 'roofing', 'shingles', 'siding', 'gutter',
      'door', 'window', 'lock', 'deadbolt', 'hinge', 'knob',
      'flooring', 'tile', 'hardwood', 'laminate', 'vinyl',
      'paint', 'primer', 'stain', 'varnish', 'caulk', 'sealant',
      'screw', 'nail', 'bolt', 'nut', 'washer', 'anchor', 'fastener',
    ],
  },

  // ── Garden, outdoor, lawn ──────────────────────────────────────────────────
  {
    name: 'garden',
    stores: ['amazon', 'homedepot', 'walmart'],
    keywords: [
      'lawn mower', 'leaf blower', 'hedge trimmer', 'chainsaw', 'weed eater',
      'garden hose', 'sprinkler', 'irrigation', 'soil', 'mulch', 'fertilizer',
      'plant pot', 'planter', 'seeds', 'bulbs', 'gardening gloves',
      'shovel', 'rake', 'hoe', 'trowel', 'pruner', 'shears',
      'patio furniture', 'outdoor chair', 'garden table', 'umbrella',
      'grill', 'bbq', 'smoker', 'fire pit', 'outdoor lighting',
      'pest control', 'weed killer', 'insecticide', 'herbicide',
    ],
  },

  // ── Clothing, fashion, accessories ────────────────────────────────────────
  {
    name: 'fashion',
    stores: ['amazon', 'walmart', 'target'],
    keywords: [
      'shirt', 't-shirt', 'blouse', 'top', 'sweater', 'hoodie', 'jacket',
      'coat', 'dress', 'skirt', 'pants', 'jeans', 'shorts', 'leggings',
      'shoes', 'sneakers', 'boots', 'sandals', 'heels', 'loafers',
      'socks', 'underwear', 'bra', 'lingerie', 'swimsuit', 'bikini',
      'hat', 'cap', 'beanie', 'gloves', 'scarf', 'belt', 'wallet',
      'handbag', 'purse', 'backpack', 'luggage', 'tote bag',
      'sunglasses', 'watch', 'jewelry', 'necklace', 'bracelet', 'ring',
      'nike', 'adidas', 'under armour', 'levi\'s', 'gap', 'old navy',
    ],
  },

  // ── Toys, kids, baby ──────────────────────────────────────────────────────
  {
    name: 'toys',
    stores: ['amazon', 'walmart', 'target'],
    keywords: [
      'toy', 'lego', 'barbie', 'hot wheels', 'nerf', 'playset',
      'action figure', 'doll', 'stuffed animal', 'plush', 'puzzle',
      'board game', 'card game', 'outdoor toy', 'bike', 'scooter',
      'baby', 'infant', 'toddler', 'diaper', 'formula', 'stroller',
      'car seat', 'baby monitor', 'crib', 'high chair', 'pacifier',
      'kids', 'children', 'educational toy', 'learning', 'school supplies',
    ],
  },

  // ── Food, grocery, beverages ───────────────────────────────────────────────
  {
    name: 'grocery',
    stores: ['amazon', 'walmart', 'target'],
    keywords: [
      'coffee', 'tea', 'keurig pod', 'nespresso', 'instant coffee',
      'snack', 'chips', 'candy', 'chocolate', 'protein bar', 'granola',
      'cereal', 'oatmeal', 'pasta', 'rice', 'flour', 'sugar', 'salt',
      'cooking oil', 'olive oil', 'vinegar', 'sauce', 'condiment',
      'canned food', 'soup', 'broth', 'spice', 'seasoning', 'herb',
      'water', 'soda', 'juice', 'energy drink', 'sports drink',
      'pet food', 'dog food', 'cat food', 'dog treat', 'cat treat',
    ],
  },

  // ── Kitchen, appliances, home ──────────────────────────────────────────────
  // Air fryer, stand mixer etc. are sold by ALL stores
  {
    name: 'kitchen',
    stores: ['amazon', 'walmart', 'homedepot', 'target'],
    keywords: [
      'air fryer', 'instant pot', 'pressure cooker', 'slow cooker', 'crockpot',
      'stand mixer', 'kitchenaid', 'blender', 'food processor', 'juicer',
      'coffee maker', 'espresso machine', 'keurig', 'nespresso machine',
      'toaster', 'toaster oven', 'microwave', 'rice cooker', 'waffle maker',
      'refrigerator', 'dishwasher', 'washing machine', 'dryer', 'vacuum',
      'roomba', 'dyson', 'shark vacuum', 'steam mop', 'carpet cleaner',
      'cookware', 'pan', 'pot', 'skillet', 'wok', 'baking',
      'knife', 'cutting board', 'utensil', 'spatula', 'ladle',
      'storage container', 'tupperware', 'meal prep', 'food storage',
    ],
  },

  // ── Office, school, stationery ─────────────────────────────────────────────
  {
    name: 'office',
    stores: ['amazon', 'walmart', 'target'],
    keywords: [
      'printer', 'ink cartridge', 'toner', 'paper', 'notebook', 'planner',
      'pen', 'pencil', 'marker', 'highlighter', 'stapler', 'tape',
      'desk', 'office chair', 'monitor stand', 'desk organizer',
      'file folder', 'binder', 'label', 'envelope', 'shipping supply',
    ],
  },

  // ── Sports, fitness, outdoors ──────────────────────────────────────────────
  {
    name: 'sports',
    stores: ['amazon', 'walmart', 'target'],
    keywords: [
      'yoga mat', 'dumbbell', 'weight', 'resistance band', 'foam roller',
      'treadmill', 'exercise bike', 'elliptical', 'rowing machine',
      'gym bag', 'water bottle', 'protein shaker', 'sports bottle',
      'tent', 'sleeping bag', 'camping', 'hiking', 'backpacking',
      'fishing', 'hunting', 'archery', 'golf', 'tennis', 'basketball',
      'football', 'soccer', 'baseball', 'hockey', 'swimming',
    ],
  },

  // ── Premium beauty — Ulta + Sephora + Macy's ──────────────────────────────
  // These brands are primarily sold at specialty beauty retailers
  {
    name: 'beauty_specialty',
    stores: ['ulta', 'sephora', 'macys', 'amazon', 'target'],
    keywords: [
      // Premium skincare
      'drunk elephant', 'tatcha', 'sunday riley', 'glow recipe', 'paulas choice',
      'la mer', 'sk-ii', 'kiehls', 'origins', 'fresh skincare',
      'the ordinary', 'inkey list', 'cosrx', 'laneige', 'innisfree',
      // Premium makeup
      'charlotte tilbury', 'nars', 'urban decay', 'too faced', 'tarte',
      'benefit cosmetics', 'mac cosmetics', 'bobbi brown', 'clinique',
      'estee lauder', 'lancome', 'dior beauty', 'ysl beauty', 'givenchy beauty',
      'fenty beauty', 'rare beauty', 'ilia beauty', 'merit beauty',
      // Hair care specialty
      'olaplex', 'kerastase', 'moroccanoil', 'living proof', 'bumble and bumble',
      'redken', 'matrix', 'joico', 'pureology',
      // Fragrance
      'perfume', 'cologne', 'eau de parfum', 'eau de toilette', 'fragrance set',
      'gift set beauty', 'beauty gift',
    ],
  },

  // ── Mass beauty — skip Sephora, keep Ulta + general stores ───────────────
  {
    name: 'beauty_mass',
    stores: ['ulta', 'macys', 'amazon', 'walmart', 'target'],
    keywords: [
      'cerave', 'neutrogena', 'olay', 'loreal', 'maybelline', 'revlon',
      'covergirl', 'nyx cosmetics', 'e.l.f.', 'wet n wild', 'milani',
      'garnier', 'dove beauty', 'nivea', 'aveeno', 'vanicream',
      'clean & clear', 'st. ives', 'queen helene',
    ],
  },

  // ── Fashion / apparel — add Macy's ────────────────────────────────────────
  {
    name: 'fashion_dept',
    stores: ['amazon', 'walmart', 'target', 'macys'],
    keywords: [
      'calvin klein', 'ralph lauren', 'tommy hilfiger', 'michael kors',
      'kate spade', 'coach', 'dkny', 'anne klein', 'charter club',
      'dress shirt', 'blazer', 'suit', 'formal wear', 'evening gown',
      'handbag', 'designer bag', 'luxury watch', 'fine jewelry',
    ],
  },

  // ── Furniture, home decor ──────────────────────────────────────────────────
  {
    name: 'furniture',
    stores: ['amazon', 'walmart', 'target'],
    keywords: [
      'sofa', 'couch', 'loveseat', 'recliner', 'armchair', 'ottoman',
      'bed frame', 'mattress', 'pillow', 'bedding', 'duvet', 'comforter',
      'dresser', 'nightstand', 'bookshelf', 'bookcase', 'tv stand',
      'coffee table', 'dining table', 'dining chair', 'bar stool',
      'curtain', 'blinds', 'rug', 'carpet', 'lamp', 'ceiling light',
      'candle', 'wall art', 'picture frame', 'mirror', 'clock',
      'throw blanket', 'throw pillow', 'towel', 'bath mat', 'shower curtain',
    ],
  },

];

// ── Main router function ──────────────────────────────────────────────────────
export function routeQuery(query, activeStoreIds = ALL_STORES) {
  const q = query.toLowerCase();

  // Find all matching categories
  const matched = CATEGORY_ROUTES.filter(cat =>
    cat.keywords.some(kw => q.includes(kw))
  );

  if (matched.length === 0) {
    // Unknown category — send to all active stores (safe fallback)
    console.log(`[Router] "${query}" → no category match → all stores`);
    return activeStoreIds;
  }

  // Union of all matched category stores, filtered to active stores only
  const routedStores = [...new Set(
    matched.flatMap(cat => cat.stores)
  )].filter(id => activeStoreIds.includes(id));

  const skipped = activeStoreIds.filter(id => !routedStores.includes(id));

  console.log(`[Router] "${query}" → categories: ${matched.map(c => c.name).join(', ')} → stores: [${routedStores.join(', ')}] → skipped: [${skipped.join(', ')}]`);

  return routedStores;
}

export default routeQuery;
